import { Button } from "@admin/components/atoms/Button";
import { Input } from "@admin/components/atoms/Input";
import { Info } from "@admin/components/molecules/Person";
import {
  DataTable,
  DataTableColumn,
  DataTableProps,
} from "@admin/components/molecules/Table";
import {
  calculateShiftDuration,
  CompanyLocation,
  formatDuration,
  getHoursAndMinutes,
  getNormalizedStartEnd,
  Shift,
  UpdateBulkShift,
  updateBulkShift,
  UpdateShiftsDto,
} from "@enos-monorepo/lib";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { DeleteShiftModal, Hours } from "../../components";
import { ClockInAgainModal } from "./Modals/ClockInAgainModal";

interface CompletedShiftsTableProps
  extends Omit<DataTableProps<Shift>, "columns"> {
  selectedLocation?: CompanyLocation;
  companyId: string;
}

export function CompletedShiftsTable({
  selectedLocation,
  companyId,
  ...props
}: CompletedShiftsTableProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [selectedShift, setSelectedShift] = useState<Shift>();
  const [isDeleteShiftModalOpen, setIsDeleteShiftModalOpen] = useState(false);
  const [isClockInAgainModalOpen, setIsClockInAgainModalOpen] = useState(false);

  const [editShifts, setEditShifts] = useState<Shift[]>([]);

  const selectedDaysShifts = useMemo(() => {
    return props.data?.map((shift) => {
      return {
        ...shift,
        plannedStartDate: getHoursAndMinutes(shift.plannedStartDate),
        plannedEndDate: getHoursAndMinutes(shift.plannedEndDate),
        workStartDate: getHoursAndMinutes(shift.workStartDate),
        workEndDate: getHoursAndMinutes(shift.workEndDate),
      };
    });
  }, [props.data]);

  useEffect(() => {
    setEditShifts(selectedDaysShifts ?? []);
  }, [selectedDaysShifts]);

  const { mutate } = useMutation({
    mutationFn: (shift: UpdateBulkShift) => updateBulkShift(shift),
    onSuccess: () => {
      toast.success(t("common.The changes have been saved"));
      queryClient.invalidateQueries({ queryKey: ["shifts"] });
    },
    onError: () => {
      toast.error(t("shift.Unable to plan shifts"));
    },
  });

  const onSave = (data: Shift[]) => {
    const bulkshifts: UpdateBulkShift = {
      updateShiftDtos: data.map((shift) => {
        const [startTime, endTime] = getNormalizedStartEnd(
          shift.plannedStartDate,
          shift.plannedEndDate,
        );

        const [workingStartTime, workingEndTime] = getNormalizedStartEnd(
          shift.workStartDate,
          shift.workEndDate,
        );

        return {
          id: shift.id,
          plannedStartTime: startTime ? startTime : shift.plannedStartDate,
          plannedEndTime: endTime ? endTime : shift.plannedEndDate,
          workStartDate: workingStartTime
            ? workingStartTime
            : shift.workStartDate,
          workEndDate: workingEndTime ? workingEndTime : shift.workEndDate,
          breakDuration: shift.breakDuration,
          actualPayment: shift.actualPayment,
          plannedPayment: shift.plannedPayment,
          companyId: companyId,
          companyLocationId: selectedLocation?.id ?? "",
          userId: shift.userId,
        };
      }) as UpdateShiftsDto[],
    };

    mutate(bulkshifts);
  };

  const endedShiftsColumns: DataTableColumn<Shift>[] = useMemo(
    () => [
      {
        header: t("common.Personnel"),
        accessorKey: "firstName",
        size: 225,
        cell: ({ row }) => (
          <Info
            userPhoto={row.original.userPhoto}
            name={row.original.firstName + " " + row.original.lastName}
            userName={row.original.userName}
          />
        ),
      },
      {
        header: t("navigation.Planned Shift"),
        accessorKey: "plannedStartDate",
        cell: ({ row }) => (
          <div className="flex gap-2.5 flex-wrap justify-center">
            <Input
              value={row.original.plannedStartDate}
              size="sm"
              type="time"
              className="w-fit"
              onChange={(e) => {
                setEditShifts((prev) =>
                  prev.map((shift) =>
                    shift.id === row.original.id
                      ? { ...shift, plannedStartDate: e.target.value }
                      : shift,
                  ),
                );
              }}
            />
            <Input
              value={row.original.plannedEndDate}
              size="sm"
              className="w-fit"
              type="time"
              onChange={(e) => {
                setEditShifts((prev) =>
                  prev.map((shift) =>
                    shift.id === row.original.id
                      ? { ...shift, plannedEndDate: e.target.value }
                      : shift,
                  ),
                );
              }}
            />
          </div>
        ),
      },
      {
        header: t("shift.Actual Shift"),
        accessorKey: "workStartDate",
        align: "center",
        cell: ({ row }) => (
          <div className="flex gap-2.5 flex-wrap justify-center">
            <Input
              value={row.original.workStartDate}
              size="sm"
              type="time"
              className="w-fit"
              onChange={(e) => {
                setEditShifts((prev) =>
                  prev.map((shift) =>
                    shift.id === row.original.id
                      ? { ...shift, workStartDate: e.target.value }
                      : shift,
                  ),
                );
              }}
            />
            <Input
              value={row.original.workEndDate}
              size="sm"
              className="w-fit"
              type="time"
              onChange={(e) => {
                setEditShifts((prev) =>
                  prev.map((shift) =>
                    shift.id === row.original.id
                      ? { ...shift, workEndDate: e.target.value }
                      : shift,
                  ),
                );
              }}
            />
          </div>
        ),
      },
      {
        header: t("shift.Break"),
        size: 120,
        accessorKey: "breakDuration",
        align: "center",
        cell: ({ row }) => (
          <div className="w-full flex">
            <Input
              placeholder="-"
              value={row.original.breakDuration?.toString()}
              size="sm"
              onChange={(e) => {
                setEditShifts((prev) =>
                  prev.map((shift) =>
                    shift.id === row.original.id
                      ? { ...shift, breakDuration: +e.target.value }
                      : shift,
                  ),
                );
              }}
            />
          </div>
        ),
      },
      {
        header: t("shift.Amount"),
        align: "center",
        size: 120,
        cell: ({ row }) => {
          return (
            <div className="flex flex-row justify-center">
              <Hours>
                {formatDuration(
                  calculateShiftDuration(
                    row.original.workStartDate,
                    row.original.workEndDate,
                    row.original.breakDuration,
                  ),
                )}
              </Hours>
            </div>
          );
        },
      },
      {
        header: t("common.Actions"),
        align: "center",
        size: 150,
        cell: () => (
          <div className="flex flex-row justify-center">
            <Button
              size="sm"
              color="green"
              onClick={() => setIsClockInAgainModalOpen(true)}
            >
              {t("shift.Clock In Again")}
            </Button>
          </div>
        ),
      },
      {
        id: "ExternalActions",
        size: 50,
        cell: () => (
          <i
            onClick={() => setIsDeleteShiftModalOpen(true)}
            className="ki-filled text-red-500 ki-trash-square text-xl cursor-pointer"
          ></i>
        ),
      },
    ],
    [t],
  );

  return (
    <>
      <DataTable
        loading={props.loading}
        columns={endedShiftsColumns}
        data={editShifts}
        header={
          <div className="flex gap-5">
            <Button
              onClick={() => setEditShifts(selectedDaysShifts ?? [])}
              variant="outline"
              size="sm"
              property="danger"
            >
              {t("common.Discard Changes")}
            </Button>
            <Button
              variant="outline"
              onClick={() => onSave(editShifts)}
              size="sm"
            >
              {t("common.Save Changes")}
            </Button>
          </div>
        }
        title={t("shift.Today's Completed Shifts")}
        onRowClick={setSelectedShift}
      />
      <DeleteShiftModal
        open={isDeleteShiftModalOpen}
        onClose={() => setIsDeleteShiftModalOpen(false)}
        shift={selectedShift}
        shiftType={t("shift.Completed")}
      />
      <ClockInAgainModal
        open={isClockInAgainModalOpen}
        onClose={() => setIsClockInAgainModalOpen(false)}
        shift={props.data?.find((shift) => shift.id === selectedShift?.id)}
        companyId={companyId}
        companyLocationId={selectedLocation?.id ?? ""}
      />
    </>
  );
}
